import React, { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Clock, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { AppButton, Callout, ConfirmModal, IconButton } from "../ui";
import Shimmer from "../Common/Shimmer";
import { adminDeleteSession, adminListSessions, adminReorderSessions } from "../../utils/smartLearning";
import { formatMinutes } from "./syllabusModel";
import SessionEditorDialog from "./SessionEditorDialog";

/**
 * ModuleSessions — the hand-authored Smart Learning sessions of one module
 * (courses whose sessions are authored rather than formed from the syllabus).
 * Sessions attach to the SAVED module's number, so this shows only for a saved,
 * unchanged syllabus.
 *
 * Props: courseId, moduleNumber, moduleTitle, codingEnabled, onCountChange(n)
 */
const ModuleSessions = ({ courseId, moduleNumber, moduleTitle, codingEnabled, onCountChange }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // session | 'new' | null
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const data = await adminListSessions({ courseId, moduleNumber });
      const rows = (data.sessions || []).sort((a, b) => a.order - b.order);
      setSessions(rows);
      onCountChange?.(rows.length);
    } catch (err) {
      setError(err?.response?.data?.message || "This module’s sessions could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [courseId, moduleNumber, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const move = async (index, delta) => {
    const to = index + delta;
    if (to < 0 || to >= sessions.length) return;
    const next = [...sessions];
    const [item] = next.splice(index, 1);
    next.splice(to, 0, item);
    setSessions(next);
    setBusy(true);
    try {
      await adminReorderSessions({ courseId, moduleNumber, orderedIds: next.map((s) => s._id) });
    } catch (err) {
      setError(err?.response?.data?.message || "The new order was not saved.");
      load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const target = removing;
    setRemoving(null);
    setBusy(true);
    try {
      await adminDeleteSession(target._id);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "The session was not removed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tw-flex tw-flex-col tw-gap-2.5 tw-rounded-md tw-border tw-border-solid tw-border-primary/25 tw-bg-primary/5 tw-p-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <div className="tw-flex tw-min-w-0 tw-flex-col">
          <h4 className="tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-text-[0.8125rem] tw-font-semibold tw-text-content">
            <Sparkles size={13} aria-hidden="true" className="tw-text-primary" />
            Smart Learning sessions <span className="tw-font-normal tw-text-content-muted">· {sessions.length}</span>
          </h4>
          <span className="tw-text-xs tw-text-content-muted">Each with its documents, assignments, quizzes, coding questions and unlock rules.</span>
        </div>
        <AppButton size="sm" onClick={() => setEditing("new")} disabled={busy}>
          <Plus size={13} aria-hidden="true" /> Add session
        </AppButton>
      </div>

      {error && <Callout tone="danger" role="alert">{error}</Callout>}
      {loading && <Shimmer height={40} count={2} />}
      {!loading && !sessions.length && (
        <p className="tw-m-0 tw-text-xs tw-text-content-muted">
          No sessions yet. Each session is one class, with the resources students get from it.
        </p>
      )}
      {!loading && sessions.length > 0 && (
        <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
          {sessions.map((s, i) => (
            <li key={s._id} className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-surface-sunken tw-px-2.5 tw-py-1.5">
              <span className="tw-w-6 tw-shrink-0 tw-text-xs tw-font-semibold tw-text-content-subtle">{i + 1}</span>
              <span className="tw-min-w-0 tw-flex-1">
                <span className="tw-block tw-truncate tw-text-[0.8125rem] tw-font-medium tw-text-content">{s.title}</span>
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-content-muted">
                  <Clock size={11} aria-hidden="true" /> {formatMinutes(s.plannedDurationMinutes || 60)}
                  {(s.resources || []).length > 0 && ` · ${s.resources.length} resource${s.resources.length === 1 ? "" : "s"}`}
                </span>
              </span>
              <IconButton size="xs" icon={ArrowUp} label={`Move ${s.title} up`} onClick={() => move(i, -1)} disabled={busy || i === 0} />
              <IconButton size="xs" icon={ArrowDown} label={`Move ${s.title} down`} onClick={() => move(i, 1)} disabled={busy || i === sessions.length - 1} />
              <IconButton size="xs" icon={Pencil} label={`Edit ${s.title}`} onClick={() => setEditing(s)} disabled={busy} />
              <IconButton size="xs" icon={Trash2} variant="ghost-destructive" label={`Remove ${s.title}`} onClick={() => setRemoving(s)} disabled={busy} />
            </li>
          ))}
        </ol>
      )}

      <SessionEditorDialog
        show={!!editing}
        onHide={() => setEditing(null)}
        courseId={courseId}
        moduleNumber={moduleNumber}
        moduleTitle={moduleTitle}
        codingEnabled={codingEnabled}
        session={editing === "new" ? null : editing}
        onSaved={() => load()}
      />
      <ConfirmModal
        show={!!removing}
        onHide={() => setRemoving(null)}
        onConfirm={remove}
        title="Remove this session?"
        message={`“${removing?.title || ""}” leaves the course and every batch that has not taught it yet. Batches that already taught it keep their record.`}
        confirmText="Remove session"
        variant="danger"
      />
    </section>
  );
};

export default ModuleSessions;
